/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, ReactNode, isValidElement, useMemo } from "react";
import { capitalizeFirst } from "../helper-functions";
import React from "react";

export type SummaryData = Record<string, any>;

export type Renderer =
    | ReactNode
    | Record<string, ReactNode>
    | ((data: SummaryData) => ReactNode | Record<string, ReactNode>);

export type RenderConfig = {
    accessor: string;
    defaultValue?: ReactNode;
    formatter?(val: any): ReactNode;
    renderer?: Renderer;
};

export type SummaryConfig = Record<string, RenderConfig[]>;

function getNestedProperty<T = any>(obj: Record<string, any>, path: string): T | undefined {
    return path.split(".").reduce((o, p) => (o && o[p] !== undefined ? o[p] : undefined), obj) as T;
}

function getValues(data: SummaryData, root: string, options: string[]) {
    return options.map<boolean | undefined>((o) => getNestedProperty(data, `${root}.${o}`));
}

function getSeverity(data: SummaryData, key: string) {
    const [light, moderate, heavy] = getValues(data, key, ["light", "moderate", "heavy"]);
    if (light && moderate && heavy) {
        return "Heavy";
    } else if (light && heavy) {
        return "Light to Heavy";
    } else if (light && moderate) {
        return "Light to Moderate";
    } else if (moderate && heavy) {
        return "Moderate to Heavy";
    } else if (light) {
        return "Light";
    } else if (moderate) {
        return "Moderate";
    } else if (heavy) {
        return "Heavy";
    }
    return "";
}

function getQuantity(data: SummaryData, key: string) {
    const [few, several, many] = getValues(data, key, ["few", "several", "many"]);
    if (few && several && many) {
        return "Many";
    } else if (few && many) {
        return "Few to Many";
    } else if (few && several) {
        return "Few to Several";
    } else if (several && many) {
        return "Several to Many";
    } else if (few) {
        return "Few";
    } else if (several) {
        return "Several";
    } else if (many) {
        return "Many";
    }
    return "";
}

function isFunction(value: unknown): value is Function {
    return typeof value === "function";
}

function isReactNode(value: any): value is ReactNode {
    if (isValidElement(value)) {
        return true;
    }
    if (typeof value === "string" || typeof value === "number") {
        return true;
    }
    if (value === null || value === undefined) {
        return true;
    }
    if (Array.isArray(value)) {
        return value.every(isReactNode); // Check all elements in the array
    }
    return false;
}

function parseRenderConfigs(configs: RenderConfig[], data: SummaryData, indent: string | null = " "): ReactNode[] {
    const result: ReactNode[] = [];
    for (const config of configs) {
        const { accessor, defaultValue, formatter, renderer } = config;
        // try to get the value from data
        let value = getNestedProperty(data, accessor) ?? defaultValue;

        if (accessor.includes("checked") && !value) {
            // skip when not checked
            continue;
        }
        if ((renderer === "" || (renderer && !isFunction(renderer))) && (value === null || value === undefined)) {
            // looking specifically for quantity and severity
            // otherwise skip because we don't know how to handle it
            if (accessor.includes("quantity")) {
                value = getQuantity(data, accessor.replace(/\.quantity$/, ""));
            } else if (accessor.includes("severity")) {
                value = getSeverity(data, accessor.replace(/\.severity$/, ""));
            } else {
                continue;
            }
        }

        let node = undefined;
        if (renderer && isFunction(renderer)) {
            node = renderer(data);
        } else {
            if (value === null || value === undefined) {
                continue;
            }
            if (!formatter && typeof value === "boolean" && !value) {
                continue;
            }
            if (renderer) {
                node = renderer;
            } else if (formatter) {
                node = formatter(value);
            } else {
                node = value;
            }
        }

        const key = accessor.length ? accessor : configs.indexOf(config);
        if (isReactNode(node)) {
            // return the node as is because it's valid
            result.push(
                <Fragment key={key}>
                    {indent}
                    {node}
                </Fragment>
            );
        } else {
            // assume the node is a record with a title key and the rest are {accessor:renderer}
            const { title, ...rest } = node;
            const renderConfigs: RenderConfig[] = [];
            for (const key in rest) {
                if (!isReactNode(rest[key])) {
                    continue;
                }
                renderConfigs.push({ accessor: key, renderer: rest[key] });
            }
            const subNodes = parseRenderConfigs(renderConfigs, data, indent).map((subNode, i, arr) => (
                <Fragment key={`subnode-${i}`}>
                    {subNode}
                    {i === arr.length - 1 ? null : ","}
                </Fragment>
            ));
            result.push(
                <Fragment key={key}>
                    {indent}
                    <>
                        {title}: {subNodes}.
                    </>
                </Fragment>
            );
        }
    }
    return result;
}

export const useAutoSummary = (config: SummaryConfig, data: SummaryData): ReactNode[] => {
    return useMemo(() => {
        if (!config || !data) return [];
        const result: ReactNode[] = [];
        for (const key in config) {
            result.push(
                <div key={key}>
                    <b>{capitalizeFirst(key)}</b> - {parseRenderConfigs(config[key], data)}
                </div>
            );
        }
        return result;
    }, [config, data]);
};

export const useAutoSummaryFlat = (config: SummaryConfig | undefined, data: SummaryData): ReactNode[] => {
    return useMemo(() => {
        if (!config || !data) return [];
        const result: ReactNode[] = [];
        for (const key in config) {
            result.push(
                <span key={key}>
                    <b>{capitalizeFirst(key)}</b> - {parseRenderConfigs(config[key], data)}&nbsp;
                </span>
            );
        }
        return [
            <div key="summary-container" style={{ display: "flex", flexWrap: "wrap" }}>
                {result}
            </div>,
        ];
    }, [config, data]);
};
